import { useDroppable } from "@dnd-kit/core";
import { get as getPathValue } from "lodash";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type { DroppableData } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import { FloatingDropArea } from "@circle-react-shared/BuilderUI/FloatingDropZone/FloatingDropArea";
import { isBaseBlock } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { DropZonePosition } from "@circle-react-shared/BuilderUI/Store/types";
import type { BaseBlock } from "../DataModel.types";

// Utility function to get parent block ID from FloatingDropZoneItem path
// Similar to getBlockPathInfo but for FloatingDropZone paths ending with prop names
const getParentBlockIdFromPath = (
  path: string,
  blocks: BaseBlock,
): string | null => {
  // Remove the last .props.{propertyName} to get parent block path
  // e.g., "props.children.value.1.props.leftContent" -> "props.children.value.1"
  // e.g., "props.children.value.0.props.children" -> "props.children.value.0"
  // eslint-disable-next-line sonarjs/prefer-regexp-exec -- Disabled for plugin version update. Fix when possible
  const lastPropsMatch = path.match(/^(.+)\.props\.[^.]+$/);

  if (!lastPropsMatch) {
    // If no .props.{property} pattern found, might be root level
    return null;
  }

  const parentBlockPath = lastPropsMatch[1];
  const fullPath = `blocks.${parentBlockPath}`;
  const block = getPathValue({ blocks }, fullPath) as BaseBlock | undefined;

  return isBaseBlock(block) ? block.id : null;
};

export interface FloatingDropZoneItemProps extends DropZonePosition {}

export const FloatingDropZoneItem = ({
  top,
  left,
  width,
  height,
  path,
  index,
  accepts,
  rejects,
  orientation,
}: FloatingDropZoneItemProps) => {
  const blocks = useBuilderStore(state => state.getBlocks());
  const parentBlockId = getParentBlockIdFromPath(path, blocks);

  const data: DroppableData = {
    accepts,
    rejects,
    index,
    path,
    floatingDroppable: true,
    parentBlockId,
  };

  // Keep unique ID to avoid conflicts
  const id = `${path}:${index ?? 0}`;
  const { isOver, setNodeRef } = useDroppable({ id, data });

  return (
    <div
      ref={setNodeRef}
      key={`${path}:${index}`}
      className="pointer-events-auto z-10"
      style={{
        position: "absolute",
        top,
        left,
        width,
        height,
      }}
      data-testid="builder-ui-floating-drop-zone-item"
      data-path={path}
      data-index={index}
      data-accepts={JSON.stringify(accepts)}
      data-rejects={JSON.stringify(rejects)}
    >
      <FloatingDropArea isOver={isOver} orientation={orientation} />
    </div>
  );
};
